import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceReviewPanel = ({ filters }) => {
  const [selectedReview, setSelectedReview] = useState(null);

  const performanceReviews = [];

  const getStatusBadge = (status) => {
    const badges = {
      completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircle' },
      in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: 'Clock' },
      pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: 'AlertCircle' },
      overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: 'XCircle' }
    };
    return badges?.[status] || badges?.pending;
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-emerald-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-amber-600';
    return 'text-red-600';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.5) return 'Exceptional';
    if (rating >= 4.0) return 'Exceeds Expectations';
    if (rating >= 3.5) return 'Meets Expectations';
    if (rating >= 3.0) return 'Needs Improvement';
    return 'Unsatisfactory';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Icon name="ClipboardCheck" size={28} className="text-blue-600" />
              Performance Reviews
            </h2>
            <p className="text-slate-600 mt-1">360-degree feedback with competency evaluations and PNG Employment Act compliance</p>
          </div>
          <Button variant="primary" iconName="Plus">
            Initiate Review
          </Button>
        </div>

        {/* Review Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-900">Completed Reviews</span>
              <Icon name="CheckCircle" size={18} className="text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">In Progress</span>
              <Icon name="Clock" size={18} className="text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-900">Pending</span>
              <Icon name="AlertCircle" size={18} className="text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-900">Avg Rating</span>
              <Icon name="Star" size={18} className="text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">0.0</p>
          </div>
        </div>
      </div>
      {/* PNG Employment Act Compliance Notice */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-blue-900 mb-2">PNG Employment Act 1978 Compliance</h3>
            <p className="text-blue-800 mb-3">
              All performance reviews comply with Section 42 requirements for fair and documented performance management procedures.
              Reviews include objective criteria, employee feedback opportunities, and documented improvement plans where necessary.
            </p>
            <Button variant="outline" iconName="FileText" size="sm">
              View Compliance Guidelines
            </Button>
          </div>
        </div>
      </div>
      {/* Reviews List */}
      {performanceReviews?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="ClipboardCheck" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Performance Reviews Yet</h3>
            <p className="text-slate-600 mb-6">Start conducting performance reviews to track employee development and growth</p>
            <Button variant="primary" iconName="Plus">
              Initiate Review
            </Button>
          </div>
        </div>
      ) : (
      <div className="space-y-4">
        {performanceReviews?.map((review) => {
          const statusBadge = getStatusBadge(review?.status);
          const ratingColor = getRatingColor(review?.overallRating);
          const ratingLabel = getRatingLabel(review?.overallRating);

          return (
            <div key={review?.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-lg transition-shadow">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{review?.employeeName}</h3>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${statusBadge?.color}`}>
                      <Icon name={statusBadge?.icon} size={14} />
                      {statusBadge?.label}
                    </span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                      {review?.reviewType}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <Icon name="Hash" size={14} />
                      <span>{review?.employeeNumber}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Building" size={14} />
                      <span>{review?.department}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Briefcase" size={14} />
                      <span>{review?.position}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>{review?.reviewPeriod}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600 mb-1">Overall Rating</p>
                  <p className={`text-3xl font-bold ${ratingColor}`}>{review?.overallRating}</p>
                  <p className={`text-xs font-semibold ${ratingColor}`}>{ratingLabel}</p>
                </div>
              </div>
              {/* Competency Ratings */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Competency Evaluations</h4>
                <div className="space-y-2">
                  {review?.competencies?.map((comp, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{comp?.name}</span>
                          <span className="text-sm font-bold text-slate-900">{comp?.rating}/5</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${getRatingColor(comp?.rating) === 'text-emerald-600' ? 'from-emerald-500 to-emerald-600' : getRatingColor(comp?.rating) === 'text-blue-600' ? 'from-blue-500 to-blue-600' : 'from-amber-500 to-amber-600'}`}
                            style={{ width: `${(comp?.rating / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 min-w-[60px] text-right">Weight: {comp?.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* 360-Degree Feedback */}
              {review?.reviewType === '360-degree' && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">360-Degree Feedback</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {review?.feedback?.supervisor && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1">
                          <Icon name="User" size={12} />
                          Supervisor Feedback
                        </p>
                        <p className="text-sm text-blue-800">{review?.feedback?.supervisor}</p>
                      </div>
                    )}
                    {review?.feedback?.peers && (
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <p className="text-xs font-semibold text-emerald-900 mb-1 flex items-center gap-1">
                          <Icon name="Users" size={12} />
                          Peer Feedback
                        </p>
                        <p className="text-sm text-emerald-800">{review?.feedback?.peers}</p>
                      </div>
                    )}
                    {review?.feedback?.subordinates && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs font-semibold text-purple-900 mb-1 flex items-center gap-1">
                          <Icon name="UserCheck" size={12} />
                          Subordinate Feedback
                        </p>
                        <p className="text-sm text-purple-800">{review?.feedback?.subordinates}</p>
                      </div>
                    )}
                    {review?.feedback?.selfAssessment && (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1">
                          <Icon name="FileText" size={12} />
                          Self-Assessment
                        </p>
                        <p className="text-sm text-amber-800">{review?.feedback?.selfAssessment}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Development Plan */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1">
                  <Icon name="Target" size={16} />
                  Development Plan
                </h4>
                <p className="text-sm text-slate-700">{review?.developmentPlan}</p>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                <Button variant="outline" iconName="Eye" size="sm">
                  View Full Review
                </Button>
                <Button variant="outline" iconName="Download" size="sm">
                  Export PDF
                </Button>
                {review?.status === 'in_progress' && (
                  <Button variant="primary" iconName="Edit" size="sm">
                    Continue Review
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default PerformanceReviewPanel;